import React, { useEffect, useMemo, useState, type FunctionComponent } from "react";
import { CloseOutlined, MailOutlined } from "@ant-design/icons";
import { Alert, Form, message, Modal, Radio, Space, Steps, Switch, Typography } from "antd";
import dayjs, { type Dayjs } from "dayjs";
import { type IFileInfo } from "react-csv-reader";
import { useTranslation } from "react-i18next";
import { v4 as uuid } from "uuid";

import { productActions } from "@fitness-app/app-store";
import { type ProductPrice } from "@fitness-app/data-models/entities/Product";
import {
  ClientImportStatus,
  ImportDuplicatePolicy,
  type ImportPreviewRow,
  type ProductClientToImportDocument,
} from "@fitness-app/data-models/entities/ProductClientToImport";
import { EMAIL_REGEX } from "@fitness-app/utils/src/constants/emailRegex";
import { generateUniqId } from "@fitness-app/utils/src/helpers/generateUniqId";

import { useAppDispatch, useAppSelector } from "~/store/initializeStore";
import { decodeAccessSource, importRowKey as rowKey, type AccessSourceValue } from "./accessSource";
import AddClientsToProduct from "./steps/AddClientsToProduct";
import ParseClientsList from "./steps/ParseClientsList";
import SelectImportFile from "./steps/SelectImportFile";
import { useImportPreview, type PreviewGroups } from "./useImportPreview";

interface OwnProps {
  visible: boolean;
  close: () => void;
  isFree: boolean;
}

type Props = OwnProps;

const { Step } = Steps;

enum StepType {
  SelectFile,
  ParseList,
  AddToProduct,
}

export interface HeaderMatcherForm {
  email: string;
  subscribeDate: string;
  firstName: string;
  lastName: string;
  phoneNumber: string;
  name: string;
  hasLastNameTogether: boolean;
  /** Encoded `AccessSourceValue` — see `accessSource.ts`. */
  accessSource: string;
}

export type File = {
  data: Record<string, string>[];
  fileInfo: IFileInfo;
} | null;

/** Why a row will not be sent, shown inline in step 2. */
export type RowProblem = "wrongDate" | "noSourceAccess" | "invalidEmail" | null;

export interface TableModel {
  email: string;
  name: string;
  subscribedDate: dayjs.Dayjs;
  accessTo: null | dayjs.Dayjs;
  accessSource: AccessSourceValue | null;
  phoneNumber?: string | null;
  problem: RowProblem;
  /** From the dry run: the row already in the target product and the source access, if any. */
  preview: ImportPreviewRow | null;
  id?: string;
}

export type ManualItemModel = TableModel & {
  id: string;
};

const LIMIT_FOR_SENDING_EMAIL_BY_MAILINGR = 500;

const accessToFromPrice = (price: ProductPrice | undefined, subscribedDate: Dayjs) =>
  price?.accessPeriod ? subscribedDate.add(price.accessPeriod.interval_count, price.accessPeriod.interval) : null;

const ImportClientsModal: FunctionComponent<Props> = ({ visible, close, isFree }) => {
  const { t } = useTranslation(["products", "common"]);
  const [currentStep, changeStep] = useState<StepType>(StepType.SelectFile);
  const [formController] = Form.useForm<HeaderMatcherForm>();
  const [uploadedFile, setFile] = useState<File>(null);
  const [formModel, setFormModel] = useState<HeaderMatcherForm | null>(null);
  const dispatch = useAppDispatch();
  const [sendEmailAboutAccount, toggleSendEmailAboutAccount] = useState(!isFree);
  const [followSource, setFollowSource] = useState(true);
  const [duplicatePolicy, setDuplicatePolicy] = useState<ImportDuplicatePolicy>(ImportDuplicatePolicy.Skip);
  const [sourceExceptions, setSourceExceptions] = useState<Record<string, AccessSourceValue>>({});
  const [emailExceptions, setEmailExceptions] = useState<Record<string, string>>({});
  const [nameExceptions, setNameExceptions] = useState<Record<string, string>>({});
  const [phoneExceptions, setPhoneExceptions] = useState<Record<string, string>>({});
  const [dateAccessToExceptions, setDateAccess] = useState<Record<string, Dayjs | null>>({});
  const [dateSubscribedToExceptions, setDateSubscribed] = useState<Record<string, Dayjs | null>>({});
  const productDetails = useAppSelector((store) => store.product.details);
  const [scheduledImportId, setScheduledImportId] = useState<string | null>(null);
  const [selectedClients, setSelectedClients] = useState<string[]>([]);
  const [manuallyAdded, addClientManually] = useState<ManualItemModel[]>([]);

  const prices = productDetails?.prices;
  const productId = productDetails?.id ?? "";
  const headerSource = useMemo(() => decodeAccessSource(formModel?.accessSource), [formModel]);
  const defaultSource: AccessSourceValue | null = useMemo(
    () => headerSource ?? (prices?.[0] ? { kind: "price", priceId: prices[0].id } : null),
    [headerSource, prices],
  );

  // Rows before the dry run: identity, dates and the access source of each row.
  const baseModel: Omit<TableModel, "accessTo" | "problem" | "preview">[] = useMemo(() => {
    if (!formModel && !manuallyAdded.length) {
      return [];
    }
    const data = uploadedFile?.data ?? [];

    const fromFile = formModel
      ? data.map((item) => {
          const clientEmail = item[formModel.email]?.toLowerCase().trim() ?? "";
          const name = formModel.hasLastNameTogether
            ? (item[formModel.firstName ?? ""] ?? "")
            : `${item[formModel.firstName] ?? ""} ${item[formModel.lastName] ?? ""}`.trim();

          return {
            email: clientEmail,
            name,
            phoneNumber: item[formModel.phoneNumber] ?? "",
            subscribedDate: dayjs(item[formModel.subscribeDate]),
            accessSource: isFree ? null : (sourceExceptions[clientEmail] ?? headerSource),
          };
        })
      : [];

    const fromManualForm = manuallyAdded.map((item) => ({
      ...item,
      email: emailExceptions[item.id] ?? item.email,
      name: nameExceptions[item.id] ?? item.name ?? "",
      phoneNumber: phoneExceptions[item.id] ?? item.phoneNumber ?? "",
      subscribedDate: dateSubscribedToExceptions[item.id] || item.subscribedDate,
      accessSource: isFree ? null : (sourceExceptions[item.id] ?? defaultSource),
    }));

    return [...fromFile, ...fromManualForm];
  }, [
    formModel,
    uploadedFile,
    sourceExceptions,
    headerSource,
    defaultSource,
    manuallyAdded,
    dateSubscribedToExceptions,
    emailExceptions,
    nameExceptions,
    phoneExceptions,
    isFree,
  ]);

  const previewGroups: PreviewGroups = useMemo(() => {
    const groups: PreviewGroups = { "": [] };

    for (const row of baseModel) {
      if (!row.email.match(EMAIL_REGEX)) {
        continue;
      }
      groups[""]?.push(row.email);

      if (row.accessSource?.kind === "linked") {
        (groups[row.accessSource.sourceProductId] ??= []).push(row.email);
      }
    }

    return groups;
  }, [baseModel]);

  const preview = useImportPreview(productId, previewGroups, currentStep === StepType.ParseList);

  const tableModel: TableModel[] = useMemo(
    () =>
      baseModel.map((row) => {
        const key = rowKey(row);
        const manualDate = dateAccessToExceptions[key];
        const source = row.accessSource;
        const target = preview.lookup(row.email, null);
        const sourceRow = source?.kind === "linked" ? preview.lookup(row.email, source.sourceProductId) : null;

        let accessTo: Dayjs | null = null;

        if (source?.kind === "linked") {
          accessTo = sourceRow?.sourceAccess?.endsAt ? dayjs.unix(sourceRow.sourceAccess.endsAt) : null;
        } else if (source?.kind === "price") {
          accessTo =
            manualDate ??
            accessToFromPrice(
              prices?.find((price) => price.id === source.priceId),
              row.subscribedDate,
            );
        } else if (source?.kind === "manual") {
          accessTo = manualDate ?? null;
        }

        let problem: RowProblem = null;

        if (!row.email.match(EMAIL_REGEX)) {
          problem = "invalidEmail";
        } else if (source?.kind === "linked" && sourceRow && !sourceRow.sourceAccess?.exists) {
          problem = "noSourceAccess";
        } else if (source?.kind !== "linked" && accessTo && accessTo.isBefore(dayjs().startOf("day"))) {
          problem = "wrongDate";
        }

        return { ...row, accessTo, problem, preview: target ?? sourceRow ?? null };
      }),
    [baseModel, dateAccessToExceptions, prices, preview],
  );

  const hasLinkedRows = tableModel.some((row) => row.accessSource?.kind === "linked");
  const activeDuplicates = tableModel.filter((row) => row.preview?.existingStatus === "active").length;
  const withoutSourceAccess = tableModel.filter((row) => row.problem === "noSourceAccess").length;

  useEffect(() => {
    if (tableModel.length > LIMIT_FOR_SENDING_EMAIL_BY_MAILINGR) {
      toggleSendEmailAboutAccount(false);
    }
  }, [tableModel]);

  const handleFormSubmit = (model: HeaderMatcherForm) => {
    setFormModel(model);
    if (uploadedFile?.data) {
      setSelectedClients(
        uploadedFile?.data
          .map((item) => item[model.email.toLowerCase()]?.toLowerCase().trim())
          .filter((item): item is string => Boolean(item)),
      );
    }
    changeStep(StepType.ParseList);
  };

  const handleClientImports = async (validatedClients: TableModel[]) => {
    if (!productDetails) {
      return null;
    }
    const now = new Date().toISOString();
    const importId = uuid();
    const clientsToImport: Omit<ProductClientToImportDocument, "total" | "count">[] = validatedClients.map((item) => {
      const [firstName, lastName] = item.name.split(" ");
      const source = item.accessSource;

      return {
        email: item.email.toLowerCase(),
        priceId: source?.kind === "price" ? source.priceId : null,
        sourceProductId: source?.kind === "linked" ? source.sourceProductId : null,
        followSource: source?.kind === "linked" ? followSource : false,
        // A linked row takes its end from the source at import time, not from here.
        accessTo: source?.kind === "linked" ? null : (item.accessTo?.toISOString() ?? null),
        subscribedDate: item.subscribedDate.isValid() ? item.subscribedDate?.toISOString() : dayjs().toISOString(),
        createdAt: now,
        updatedAt: now,
        productId: productDetails.id,
        // The product's owner, not the logged-in user: an import started by a team
        // member used to carry their uid all the way into the queued task, where it
        // failed the `products_settings` lookup (TRE-1869). The service rebinds this
        // field anyway — sending the tenant keeps the two sides telling one story.
        ownerId: productDetails.owner,
        id: generateUniqId(),
        importId,
        status: ClientImportStatus.Pending,
        firstName: firstName ?? "",
        lastName: lastName ?? "",
        phoneNumber: item.phoneNumber || null,
        isFree,
        errorMessage: null,
        sendEmailAboutAccountCreation: sendEmailAboutAccount,
        duplicatePolicy,
        resultProductClientId: null,
      };
    });
    try {
      const { importId: scheduledId } = await dispatch(
        productActions.importClientsList({
          productId: productDetails.id,
          list: clientsToImport,
        }),
      ).unwrap();
      setScheduledImportId(scheduledId);
      formController?.resetFields();
    } catch (e) {
      void message.error(t("import.cannotImportClients"));
      changeStep(StepType.ParseList);
    }
  };

  const onAddNewRow = () => {
    const id = uuid();
    addClientManually((prev) => [
      ...prev,
      {
        email: "",
        name: "",
        id,
        subscribedDate: dayjs(),
        accessTo: null,
        accessSource: defaultSource,
        problem: null,
        preview: null,
      },
    ]);
    setSelectedClients((prev) => [...prev, id]);
  };

  const stepSwitch = () => {
    switch (currentStep) {
      case StepType.SelectFile:
        return (
          <SelectImportFile
            onSubmit={handleFormSubmit}
            isFree={isFree}
            formController={formController}
            file={uploadedFile}
            setFile={setFile}
            productId={productId}
            skipStep={() => {
              changeStep(StepType.ParseList);
              onAddNewRow();
            }}
          />
        );
      case StepType.ParseList:
        return (
          <>
            <Space
              align="center"
              direction="vertical"
              style={{
                justifyContent: "center",
                width: "100%",
                margin: "30px 0",
              }}
            >
              <Typography.Paragraph strong style={{ marginBottom: 0 }}>
                {t("import.sendInfoAboutAccountCreation")}
              </Typography.Paragraph>
              <Switch
                disabled={tableModel.length > LIMIT_FOR_SENDING_EMAIL_BY_MAILINGR}
                checkedChildren={<MailOutlined />}
                unCheckedChildren={<CloseOutlined />}
                checked={sendEmailAboutAccount}
                onChange={toggleSendEmailAboutAccount}
              />
              {tableModel.length > LIMIT_FOR_SENDING_EMAIL_BY_MAILINGR && (
                <Alert
                  showIcon
                  type="warning"
                  message={t("import.cannotSendEmailNotification", {
                    count: LIMIT_FOR_SENDING_EMAIL_BY_MAILINGR,
                  })}
                />
              )}
              {hasLinkedRows && (
                <Space align="center" style={{ marginTop: 12 }}>
                  <Switch checked={followSource} onChange={setFollowSource} />
                  <div>
                    <Typography.Text strong>{t("import.accessSource.followSource")}</Typography.Text>
                    <Typography.Paragraph type="secondary" style={{ marginBottom: 0 }}>
                      {t("import.accessSource.followSourceHint")}
                    </Typography.Paragraph>
                  </div>
                </Space>
              )}
            </Space>
            {activeDuplicates > 0 && (
              <Alert
                showIcon
                type="info"
                style={{ marginBottom: 16 }}
                message={t("import.preview.duplicatesTitle", { count: activeDuplicates })}
                description={
                  <Radio.Group value={duplicatePolicy} onChange={(e) => setDuplicatePolicy(e.target.value)}>
                    <Space direction="vertical" size={2}>
                      <Radio value={ImportDuplicatePolicy.Skip}>{t("import.duplicatePolicy.skip")}</Radio>
                      <Radio value={ImportDuplicatePolicy.Extend}>{t("import.duplicatePolicy.extend")}</Radio>
                      {hasLinkedRows && (
                        <Radio value={ImportDuplicatePolicy.Relink}>{t("import.duplicatePolicy.relink")}</Radio>
                      )}
                    </Space>
                  </Radio.Group>
                }
              />
            )}
            {withoutSourceAccess > 0 && (
              <Alert
                showIcon
                type="warning"
                style={{ marginBottom: 16 }}
                message={t("import.preview.noSourceAccessTitle", { count: withoutSourceAccess })}
              />
            )}
            {preview.isError && (
              <Alert showIcon type="error" style={{ marginBottom: 16 }} message={t("import.preview.error")} />
            )}
            <ParseClientsList
              productId={productId}
              selectedClients={selectedClients}
              setSelectedRows={setSelectedClients}
              tableModel={tableModel}
              addNewRow={onAddNewRow}
              isFree={isFree}
              previewLoading={preview.isFetching}
              setAccessSourceException={(key, source) =>
                setSourceExceptions((prev) => ({
                  ...prev,
                  [key]: source,
                }))
              }
              setDateException={(key, date) => setDateAccess((prev) => ({ ...prev, [key]: date }))}
              setDateSubscriberException={(id, value) => setDateSubscribed((prev) => ({ ...prev, [id]: value }))}
              setName={(id, value) => setNameExceptions((prev) => ({ ...prev, [id]: value }))}
              setEmail={(id, value) => setEmailExceptions((prev) => ({ ...prev, [id]: value }))}
              setPhone={(id, value) => setPhoneExceptions((prev) => ({ ...prev, [id]: value }))}
              removeItem={(id) => {
                addClientManually((prev) => prev.filter((item) => item.id !== id));
                setSelectedClients((prev) => prev.filter((addedId) => addedId !== id));
              }}
            />
          </>
        );
      case StepType.AddToProduct:
        return (
          <AddClientsToProduct
            tableModel={tableModel}
            productId={productId}
            importId={scheduledImportId}
            close={close}
          />
        );
      default:
        return null;
    }
  };

  const handleNextStep = () => {
    if (currentStep === StepType.SelectFile) {
      formController?.submit();
    }
    if (currentStep == StepType.ParseList) {
      const clientsToImport = tableModel.filter(
        (item) => selectedClients.includes(rowKey(item)) && item.problem === null && (isFree || item.accessSource),
      );

      if (!clientsToImport.length) {
        void message.warning(t("import.emptyList"));
        return;
      }
      changeStep(StepType.AddToProduct);
      void handleClientImports(clientsToImport);
    }
  };

  const handleCancelButton = () => {
    if (currentStep === StepType.SelectFile || currentStep === StepType.AddToProduct) {
      close();
      return;
    }

    changeStep((prev) => prev - 1);
  };

  return (
    <Modal
      width={1400}
      className="max-w-full"
      title={t("import.modalTitle")}
      visible={visible}
      onCancel={handleCancelButton}
      destroyOnClose
      onOk={handleNextStep}
      footer={currentStep === StepType.AddToProduct ? null : undefined}
      okButtonProps={{ disabled: currentStep === StepType.ParseList && preview.isFetching }}
      cancelText={currentStep === StepType.SelectFile ? t("common:button.cancel") : t("common:button.prev")}
      okText={currentStep === StepType.ParseList ? t("import.importButton") : t("import.nextButton")}
    >
      <Steps size="small" current={currentStep}>
        <Step title={t("import.selectFile")} />
        <Step title={t("import.verifyList")} />
        <Step title={t("import.addToProduct")} />
      </Steps>
      <div className="max-h-[900px] min-h-[300px] overflow-y-auto">{stepSwitch()}</div>
    </Modal>
  );
};

export default ImportClientsModal;
